<script setup>

import {Cropper} from 'vue-advanced-cropper';
import {FILE_TYPES} from '../../helpers/constants';
import 'vue-advanced-cropper/dist/style.css';
import IconTrash from "../icons/IconTrash.vue";
import IconMedia from "../icons/IconMedia.vue";
import Loading from "../Loading.vue";
import FormButton from "./../../pge-components/pge-button.vue";
import IconImageUpload from "../icons/IconImageUpload.vue";
import Modal from "../Modal.vue";

</script>

<template>
  <div class="background-backdrop">
    <div class="uploader d-none">
      <div class="uploader__title">
        <div><b>Asset Library</b></div>
        <div style="cursor: pointer;" @click="() => closeUploader()">
          <svg style="width:16px;height:16px" viewBox="0 0 24 24">
            <path fill="currentColor"
                  d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
          </svg>
        </div>
      </div>
      <div v-if="uploader.loading" class="uploader__loading-container">
        <loading v-if="uploader.loading" variant="cards" :rows="6" :on-retry="getFiles" />
      </div>
      <div class="uploader__files" v-else>
        <input :id="`field-upload-${name}`" type="file" hidden
               :name="name" @input="(e) => {$emit('update:modelValue', e.target.value)}"
               :accept="accept" @change="(e) => uploadFile(e)"/>
        <label :for="`field-upload-${name}`"
               aria-label="Upload"
               @mouseenter="defaultText = hoverText"
               @mouseleave="defaultText = labelText"
               @click.stop="">
          <div class="add-image-placeholder fileItem">
            <IconImageUpload v-if="accept ==='image/*'"/>
            <icon-media class="uploader-icon-media" v-else/>
          </div>
        </label>
        <div class="fileItem" :class="{active: selectedFile.guid === image.guid, loading: image.loading}"
             v-for="image in uploader.images"
             :key="image.id" @click="() => selectFile(image)">
          <div v-if="image.loading">
            <div role="progressbar" :aria-valuenow="percent" aria-valuemin="0" aria-valuemax="100"
                 :style="`--value: ${percent}`"></div>
          </div>
          <div v-else>
            <div v-if="image.type =='images' || image.type ==='logo' || image.type ==='avatar'" class="imageContainer">
              <img :src="image.guid" alt="">
              <span class="delete-image-container" @click="() => modalRemoveItem = image">
                <IconTrash/>
              </span>
            </div>
            <div v-else>
              <span class="lineClamp">{{ image.post_title }}</span>
              <span class="delete-image-container" @click="() => modalRemoveItem = image">
                <IconTrash/>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="uploader__footer">
        <div class="msg-tool">
          <p>{{ defaultText }}</p>
          <span v-if="accept === 'image/*'">Max size 2MB</span>
        </div>

        <div class="footer-buttons flex flex--end">
          <form-button template="text" @click="closeUploader" label="Cancel"/>
          <form-button @click="fileSelected" label="Select" :disabled="!selectedFile.id"/>
        </div>
      </div>
      <modal v-if="modalRemoveItem" title="Delete Item?" :onClose="() => modalRemoveItem = null" class="assetLibraryModal">
        <template v-slot:content>
          <p>This will permanently delete the item. This cannot be undone.</p>
        </template>
        <template v-slot:actions>
          <form-button @click="() => modalRemoveItem = null" label="Cancel"/>
          <form-button @click="() => handleDeleteImage(modalRemoveItem)" label="Delete"/>
        </template>
      </modal>
    </div>
    <modal v-if="showCropper" title="Crop Image" class="cropper-modal" @close="showCropper = false" max-width="480px">
      <template v-slot:content>
        <cropper
            ref="cropper"
            class="cropper"
            background-class="cropper__background"
            image-class="cropper__image"
            area-class="cropper__area"
            :src="cropperSrc"
            image-restriction="fit-area"
            :resizeImage="{ wheel: false }"
            :stencil-props="{
              aspectRatio: getAspectRatio,
            }"
        />
      </template>
      <template v-slot:actions>
        <form-button @click="cropImage" :disabled="btnDisabled" label="Crop"/>
      </template>
    </modal>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    value: String,
    preview: String,
    name: String,
    driver: {
      type: String,
      default: 'public'
    },
    labelText: {
      type: String,
      default: 'Select a Hub Logo'
    },
    accept: {
      type: String,
      default: 'image/*'
    },
    imageType: {
      type: String,
      default: 'images'
    },
    aspectRatio: {
      type: String,
      default: 'wide'
    },
  },
  emits: ['change', 'delete-pdf', 'close', 'delete-image'],
  data() {
    return {
      btnDisabled: false,
      modalRemoveItem: null,
      percent: 0,
      defaultText: this.labelText,
      hoverText: 'Upload',
      base64Images: [],
      mimeType: null,
      fileName: null,
      selectedFile: {
        id: null,
        guid: null,
        post_title: null,
        path: null
      },
      selectedPath: null,
      uploader: {
        images: [],
        loading: false
      },
      showCropper: false,
      cropperSrc: null,
    };
  },
  computed: {
    getAspectRatio() {
      if (this.aspectRatio === 'wide') {
        return 16 / 9;
      } else if (this.aspectRatio === 'square') {
        return 1;
      } else {
        return 4;
      }
    },
  },
  mounted() {
    this.getFiles();
    this.openUploader();
  },
  methods: {
    async getFiles() {
      this.uploader.loading = true;
      try {
        const response = await axios.get(`${window.store.event_backend_api}/media-items`, {
          params: {type: this.determineType()},
        });
        let images = response.data.data || [];

        if (this.preview && this.preview.match(/^(https?:\/\/[^\s$.?#].[^\s]*)$/)) {
          const previewFilename = this.preview.substring(this.preview.lastIndexOf('/') + 1);
          this.selectedFile = images.find(img => {
            const imgFilename = img.guid.substring(img.guid.lastIndexOf('/') + 1);
            return imgFilename === previewFilename;
          }) || {};

          images = images.filter(img => {
            const imgFilename = img.guid.substring(img.guid.lastIndexOf('/') + 1);
            return imgFilename !== previewFilename;
          });

          if (Object.keys(this.selectedFile).length) {
            images.unshift(this.selectedFile);
          }
        }

        this.uploader.images = images;
      } catch (error) {
        console.error("Failed to load files", error);
      } finally {
        this.uploader.loading = false;
      }
    },
    isImageType() {
      return this.accept === 'image/*';
    },
    async removeImage(img) {
      this.$emit('update:modelValue', null);
      if (this.isImageType()) {
        this.$emit('delete-image', img);
      }
      this.modalRemoveItem = null;
    },
    async handleDeleteImage(img) {
      if (img.id) {
        try {
          const response = await axios.post(`${window.store.event_backend_api}/media-items/delete`, {id: img.id});
          if (response.data.success) {
            this.selectedFile = {
              id: null,
              guid: null,
              post_title: null,
              path: null
            };
            this.uploader.images = this.uploader.images.filter(_img => _img.id !== img.id);
            await this.removeImage(img);
            this.$emit('delete-pdf', {url: img.guid, post_title: img.post_title});
          }
        } catch (error) {
          console.error("Error deleting image", error);
        }
      }
    },
    async determineMimeType(file) {
      return new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onloadend = (e) => {
          if (!e || !e.target || !e.target.result) return resolve({mimeType: file.type, extension: ''});
          const arr = (new Uint8Array(e.target.result)).subarray(0, 4);
          let header = "";
          for (let i = 0; i < arr.length; i++) {
            header += arr[i].toString(16);
          }
          let mimeType = file.type;
          let extension = '';
          switch (header) {
            case "89504e47":
              mimeType = "image/png";
              extension = '.png';
              break;
            case "47494638":
              mimeType = "image/gif";
              extension = '.gif';
              break;
            case "ffd8ffe0":
            case "ffd8ffe1":
            case "ffd8ffe2":
            case "ffd8ffe3":
            case "ffd8ffe8":
              mimeType = "image/jpeg";
              extension = '.jpeg';
              break;
            default:
              break;
          }
          resolve({mimeType, extension});
        };
        fr.onerror = () => {
          resolve({mimeType: file.type, extension: ''});
        };
        fr.readAsArrayBuffer(file.slice(0, 4));
      });
    },
    getCanvasBlob(canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Canvas is empty'));
          }
        }, this.mimeType);
      });
    },
    uploadFile(e) {
      const files = e.target.files;
      if (!files.length) return;

      const file = files[0];
      const acceptedTypes = this.accept.split(',');
      const isImage = file.type.startsWith('image/');
      const isPDF = file.type === FILE_TYPES.PDF;
      const isSVG = file.type === FILE_TYPES.SVG;
      const isValidType = acceptedTypes.some(type => file.type === type || (type === FILE_TYPES.IMAGES && isImage));

      if (!isValidType) {
        alert(`Invalid file type. Accepted types are: ${this.accept.replace(',', ', ')}`);
        return;
      }
      const fileReader = new FileReader();

      if (isImage && !isSVG) {
        this.determineMimeType(file).then(({mimeType, extension}) => {
          this.mimeType = mimeType;
          this.fileName = file.name || ('cropped_image' + extension);

          const fileReader = new FileReader();
          const img = new Image();
          fileReader.onload = (fileLoadedEvent) => {
            img.src = fileLoadedEvent.target.result;
          };
          img.onload = () => {
            this.cropperSrc = img.src;
            this.showCropper = true;
          };
          fileReader.readAsDataURL(file);
        });
      } else {
        const formData = new FormData();
        formData.append('driver', this.driver);
        formData.append('file', file);
        if (isSVG) formData.append('type', 'svg');
        else if (isPDF) formData.append('type', 'documents');

        fileReader.onload = async () => {
          const placeholderId = Date.now();
          const placeholderImage = {
            id: placeholderId,
            loading: true,
            strokeDashoffset: 220,
            stroke: '#06a4e8'
          };
          this.uploader.images.unshift(placeholderImage);

          try {
            const response = await axios.post(`${window.store.event_backend_api}/media-items`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              onUploadProgress: (progressEvent) => {
                this.defaultText = 'Uploading...';
                placeholderImage.loading = true;
                this.percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              }
            });

            if (response.data.data && response.data.data.guid) {
              const index = this.uploader.images.findIndex(img => img.id === placeholderId);
              if (index !== -1) {
                this.uploader.images[index] = {...response.data.data, loading: false};
                this.percent = 0;
                this.uploader.images[index].loading = false;
                this.defaultText = 'Upload Successful!';
              }
              this.selectFile({...response.data.data});
            }
          } catch (error) {
            this.handleUploadError(placeholderId, error);
          } finally {
            e.target.value = '';
          }

        };
        fileReader.readAsDataURL(file);
      }
    },
    async cropImage() {
      this.btnDisabled = true;
      const cropper = this.$refs.cropper;
      const canvas = cropper.getCanvas();

      try {
        const blob = await this.getCanvasBlob(canvas);
        const file = new File([blob], this.fileName, {type: this.mimeType});

        const formData = new FormData();
        formData.append('driver', this.driver);
        formData.append('file', file);
        formData.append('type', this.imageType);

        const placeholderId = Date.now();
        const placeholderImage = {
          id: placeholderId,
          loading: true,
          strokeDashoffset: 220,
          stroke: '#06a4e8'
        };
        this.uploader.images.unshift(placeholderImage);

        try {
          const response = await axios.post(`${window.store.event_backend_api}/media-items`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
              this.defaultText = 'Uploading...';
              placeholderImage.loading = true;
              this.percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              this.cropperSrc = null;
              this.showCropper = false;
            }
          });

          if (response.data.data && response.data.data.guid) {
            const index = this.uploader.images.findIndex(img => img.id === placeholderId);
            if (index !== -1) {
              this.uploader.images[index] = {...response.data.data, loading: false};
              this.percent = 0;
              this.uploader.images[index].loading = false;
              this.defaultText = 'Upload Successful!';
            }
            this.selectFile({...response.data.data});
          }
        } catch (error) {
          this.handleUploadError(placeholderId, error);
        } finally {
          this.cropperSrc = null;
          this.showCropper = false;
          this.btnDisabled = false;
        }

      } catch (error) {
        console.error('Error getting canvas blob', error);
        this.btnDisabled = false;
        this.cropperSrc = null;
        this.showCropper = false;
      }
    },

    handleUploadError(placeholderId, error) {
      this.percent = 0;
      const index = this.uploader.images.findIndex(img => img.id === placeholderId);
      this.uploader.images[index].loading = false;
      this.uploader.images = this.uploader.images.filter(img => img.id !== placeholderId);
      if (error.response && error.response.status === 422) {
        window.loadNotification({message: error.response.data.file[0], error: true});
      }
    },
    selectFile(image) {
      this.selectedFile = {...image};
    },
    fileSelected() {
      if (this.isImageType()) {
        this.$emit('change', this.selectedFile);
      } else {
        this.$emit('change', {url: this.selectedFile.guid, post_title: this.selectedFile.post_title});
      }
      this.closeUploader();
    },
    determineType() {
      return this.isImageType() ? (this.imageType === 'logo' ? 'logo' : (this.imageType === 'avatar' ? 'avatar' : 'images')) : 'documents';
    },
    scaleUpDialog(dialog, buttonPos) {
      const centerX = (window.innerWidth / 2) - (dialog.offsetWidth / 2);
      const centerY = (window.innerHeight / 2) - dialog.offsetHeight;

      dialog.style.transform = `translate(${centerX - buttonPos.left}px, ${centerY - buttonPos.top}px) scale(1)`;
    },
    getPosition(element) {
      const rect = element.getBoundingClientRect();
      return {
        left: rect.left,
        top: rect.top
      };
    },
    closeUploader() {
      const dialog = document.querySelector('.uploader');
      const backdrop = document.querySelector('.background-backdrop');
      dialog.style.transform = `translate(-45%, -45%) scale(0)`;

      setTimeout(() => {
        dialog.style.display = 'none';
        document.querySelector('.uploader').classList.add('d-none');
        if (backdrop) {
          backdrop.style.display = 'none';
        }

        const profileDiv = document.querySelector("#boxProfile");
        if (profileDiv) {
          profileDiv.style.zIndex = 101;
        }

        this.$emit('close');
      }, 300);
    },
    getPositionRelativeToParent(element, parent) {
      const elementRect = element.getBoundingClientRect();
      const parentRect = parent.getBoundingClientRect();

      return {
        top: elementRect.top - parentRect.top,
        left: elementRect.left - parentRect.left
      };
    },
    openUploader() {
      const button = document.querySelector('.assetLibrary');
      const dialog = document.querySelector('.uploader');
      const modal = document.querySelector('.c-modal__body');
      const backdrop = document.querySelector('.background-backdrop');
      const buttonPos = this.getPosition(button);

      const relativeButtonPos = modal ? this.getPositionRelativeToParent(button, modal) : null;

      const parentRect = dialog.parentElement.getBoundingClientRect();

      dialog.style.position = 'fixed';
      dialog.style.left = relativeButtonPos ? `${relativeButtonPos.left}px` : `${buttonPos.left}px`;
      dialog.style.top = relativeButtonPos ? `${relativeButtonPos.top + 200}px` : `${buttonPos.top + 200}px`;
      dialog.style.transform = `translate(-45%, -45%) scale(0)`;
      dialog.style.transition = 'transform 0.3s ease-out';
      dialog.style.zIndex = '1000';
      dialog.style.display = 'block';
      document.querySelector('.uploader').classList.remove('d-none');
      setTimeout(() => this.scaleUpDialog(dialog, buttonPos), 10);
      backdrop.style.position = 'fixed';
      backdrop.style.top = `-${parentRect.top}px`;
      backdrop.style.left = `-${parentRect.left}px`;
      backdrop.style.width = '100vw';
      backdrop.style.height = '100vh';
      backdrop.style.background = 'rgba(39, 38, 37, .45)';
      backdrop.style.zIndex = '1000'; // Ensure backdrop is below the dialog

      const profileDiv = document.querySelector("#boxProfile");
      if (profileDiv) {
        profileDiv.style.zIndex = 9999;
      }
    },
  }
};
</script>


<style lang="scss" scoped>

.lineClamp {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  overflow: hidden;
  line-clamp: 2;
}

.d-none {
  display: none !important;
}

@keyframes growProgressBar {
  0%, 33% {
    --pgPercentage: 0;
  }
  100% {
    --pgPercentage: var(--value);
  }
}

@property --pgPercentage {
  syntax: '<number>';
  inherits: false;
  initial-value: 0;
}

div[role="progressbar"] {
  --size: 6rem;
  --fg: #06A4E8;
  --bg: #E5DEE9;
  --pgPercentage: var(--value);
  animation: growProgressBar 0.2s ease forwards;
  width: var(--size);
  height: var(--size);
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: radial-gradient(closest-side, white 80%, transparent 0 99.9%, white 0),
  conic-gradient(var(--fg) calc(var(--pgPercentage) * 1%), var(--bg) 0);
  font-family: Helvetica, Arial, sans-serif;
  font-size: calc(var(--size) / 5);
  color: #000;

  &:before {
    counter-reset: percentage var(--value);
    content: counter(percentage) '%';
  }
}

@keyframes scaleUpFromButton {
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
}

.uploader {
  box-shadow: 0 2px 6px 2px #00000026, 0 1px 2px #0000004d;
  background-color: #fff;
  border-radius: 12px;
  position: fixed;
  overflow: hidden;
  max-width: 100%;
  min-height: 300px;
  height: auto;
  z-index: 100000000;
  transition: transform 0.3s ease-out;

  &__loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 326px;
    width: 436px;
  }

  &__title {
    justify-content: space-between;
    padding: 15px 15px 20px;
    align-items: center;
    line-height: 1.2;
    position: sticky;
    font-size: 14px;
    display: flex;
    color: #000;
    top: 0;

    label {
      display: inline-block;
      font-size: 14px;
      font-weight: 400;
      background-color: #583174;
      color: #fff;
      padding: 5px 10px 6px;
      border-radius: 10px;
      margin-bottom: 0;
      cursor: pointer;
      line-height: 1;
    }
  }

  &__files {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 13px;
    grid-row-gap: 13px;
    grid-auto-rows: 130px;
    overflow: auto;
    height: calc(100px * 3 + 26px);
    padding: 0 13px;

    .fileItem {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      border: 2px solid transparent;
      position: relative;
      height: 128px;
      width: 128px;
      border-radius: 12px;
      overflow: hidden;

      .uploader-icon-media {
        width: 35px;
      }

      &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 9px;
        background: rgba(94, 47, 120, 0.08);
        z-index: 1;
        display: none;
      }

      &:hover {
        border: 1px solid rgba(171, 164, 178, 1);

        &:after {
          display: block;
        }

        .delete-image-container {
          display: flex !important;
        }
      }

      &.loading {
        display: flex;
        border: 1px dotted #808080;
        background-color: #fff;
        border-radius: 12px;
      }

      img {
        height: 100%;
        width: 100%;
        object-fit: cover;
      }

      &.active {
        border-color: #583174;

        &:hover {
          border-width: 2px !important;
        }
      }

      .delete-image-container {
        background: rgba(255, 247, 252, .6);
        justify-content: center;
        align-items: center;
        border-radius: 100%;
        position: absolute;
        text-align: center;
        display: none;
        height: 24px;
        width: 24px;
        bottom: 5px;
        z-index: 10;
        right: 5px;

        &:hover {
          background: rgba(255, 247, 252, 0.9);
        }

        &:focus {
          background: rgba(255, 247, 252, 1);
        }
      }
    }

    .add-image-placeholder {
      border: 1px dotted #808080;
      background-color: white;
      border-radius: 12px;

      &:hover {
        background: #5E2F7814;
      }
    }
  }

  &__footer {
    align-items: center;
    position: sticky;
    line-height: 1.2;
    padding: 15px 0;

    .msg-tool {
      flex-direction: column;
      align-items: center;
      display: flex;

      p {
        line-height: 24px;
        font-size: 16px;
        margin: 0 0 5px;
      }

      span {
        line-height: 16px;
        font-size: 12px;
      }
    }

    .footer-buttons {
      justify-content: flex-end;
      padding: 25px 20px 5px;
      align-items: center;
      column-gap: 15px;
      display: flex;
    }
  }
}

.assetLibraryModal {
  width: 100%;
  height: 100%;
  min-width: auto;
}

.imageContainer {
  height: 128px;
  width: 128px;
}

.background-backdrop {
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(39, 38, 37, 0.45);
  max-width: 100vw;
}
</style>
<style lang="scss">
.cropper {
  border: solid 1px #EEE;
  min-height: 300px;
  width: 100%;

  &__background {
    background: #fff;
  }
}
</style>
